import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SonicApplicationData } from "../common/classes/SonicApplication";

import { getApplicationProjectsAdmin } from "../services/api/applications/getApplicationProjectsAdmin";
import { postUpdateApplication } from "../services/api/applications/postUpdateApplication";
import { getProjectApplications } from "../services/api/applications/getProjectApplications";
import { postUpdateMultipleApplications } from "../services/api/applications/postUpdateMultipleApplications";

export const useGetApplication = (args: {
  auth_token?: string | null;
  is_jikei_request: boolean;
  id?: string;
  status?: string;
  page?: number;
  search?: string;
  per?: number;
  sort_by?: string;
  desc?: boolean;
}) => {
  const {
    auth_token,
    is_jikei_request,
    id,
    status,
    page,
    search,
    per,
    sort_by,
    desc,
  } = args;

  const initialData = {
    applicationData: {
      total_pages: 0,
      page: 0,
      total_entries: 0,
      applications: [],
      projects: [],
    },
    getApplicationLoading: false,
    getApplicationError: false,
  };

  if (!auth_token) return initialData;

  const getApplication = useQuery(["application", args], ({ signal }) =>
    getApplicationProjectsAdmin(
      {
        auth_token: auth_token,
        is_jikei_request: is_jikei_request,
        id: id,
        status: status,
        page: page,
        per_page: per,
        search: search,
        sort_by: sort_by,
        desc: desc,
      },
      signal
    )
  );

  return {
    applicationData: getApplication.data ?? initialData.applicationData,
    getApplicationLoading: getApplication.isLoading,
    getApplicationError: getApplication.isError,
  };
};

export const useUpdateApplication = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      auth_token: string;
      is_jikei_request: boolean;
      application_id: string;
      status?: string;
      memo?: string;
      send_mail?: boolean;
    }) => {
      await postUpdateApplication(
        args.auth_token,
        args.is_jikei_request,
        args.application_id,
        args.status,
        args.memo,
        args.send_mail
      );
    },
    { onSuccess: () => queryClient.invalidateQueries(["application"]) }
  );

  return {
    updateApplication: mutation.mutate,
    updateLoading: mutation.isLoading,
  };
};

export const useGetApplicationByProject = (args: {
  auth_token?: string | null;
  project_id?: string;
  is_jikei_request: boolean;
}) => {
  const { auth_token, project_id, is_jikei_request } = args;

  const initialData: {
    applications: SonicApplicationData[];
    getApplicationError: boolean;
  } = {
    applications: [],
    getApplicationError: false,
  };

  if (!auth_token || !project_id) return initialData;

  const getApplication = useQuery(
    ["application"],
    ({ signal }) =>
      getProjectApplications(
        {
          auth_token: auth_token,
          project_id: project_id,
          is_jikei_request: is_jikei_request,
        },
        signal
      ),
    { cacheTime: 0 } // プロジェクト詳細のみで使用され、それぞれのプロジェクトで変わるので、キャッシュとして持つ意味はおそらくない
  );

  return {
    applications: getApplication.data
      ? getApplication.data.applications
      : initialData.applications,
    getApplicationError: getApplication.isError,
  };
};

export const useUpdateMultipleApplications = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      auth_token: string;
      project_id: string;
      application_ids: Array<string>;
      status: string;
      send_mail: boolean;
      is_jikei_request: boolean;
      cb: () => void;
    }) => {
      await postUpdateMultipleApplications(
        args.auth_token,
        args.project_id,
        args.application_ids,
        args.status,
        args.send_mail,
        args.is_jikei_request
      );
      args.cb();
    },
    { onSuccess: () => queryClient.invalidateQueries(["application"]) }
  );

  return {
    multipleUpdate: mutation.mutate,
  };
};
